import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { getFirestore, initializeFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PreloadAllModules, provideRouter, withComponentInputBinding, withPreloading, withRouterConfig } from '@angular/router';
// import { setLogLevel } from "firebase/app";
import { provideServiceWorker } from '@angular/service-worker';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { PendingChangesGuard } from './core/guards/pending-changes.guard';
import { demoProviders } from './demo/demo.providers';

function firebaseAppInitializer() {
  // setLogLevel("debug");
  return initializeApp(environment.firebase);
}

function shouldUseFirestoreSafariLongPollingWorkaround(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  const isSafari = /Safari\//.test(userAgent) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|OPiOS/.test(userAgent);
  // Safari 26.x can keep reporting `Version/26.0` in the user agent even when
  // the product version shown in the UI has advanced (for example 26.4).
  const isAffectedSafariVersion = /Version\/26(?:\.0)?\b/.test(userAgent);

  return isSafari && isAffectedSafariVersion;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' }), withPreloading(PreloadAllModules)),
    provideFirebaseApp(firebaseAppInitializer),
    provideAuth(() => getAuth()),
    provideFirestore(() => {
      const app = getApp();

      if (shouldUseFirestoreSafariLongPollingWorkaround()) {
        return initializeFirestore(app, {
          experimentalForceLongPolling: true,
        });
      }

      return getFirestore(app);
    }),
    provideDatabase(() => getDatabase()),
    provideFunctions(() => getFunctions()),
    provideMessaging(() => getMessaging()),
    providePerformance(() => getPerformance()),
    provideStorage(() => getStorage()),
    provideAnimationsAsync(),
    provideServiceWorker('ngsw-worker.js', {
      // Disabled in demo builds so captures never get a cached shell.
      enabled: !environment.demoMode,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    PendingChangesGuard,
    // Demo Mode DI swaps — empty array unless environment.demoMode is true (and this
    // whole module is fileReplacement-swapped to a no-op in non-demo builds).
    ...demoProviders(),
  ],
};
